import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DigitalAssistantPage from "./pages/DigitalAssistantPage";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import TeamDashboard from "./pages/TeamDashboard";
import TeamActivityPage from "./pages/TeamActivityPage";
import TeamClientDetail from "./pages/TeamClientDetail";
import PackageSelection from "./pages/PackageSelection";
import ITRFilingView from "./pages/ITRFilingView";
import AccountingView from "./pages/AccountingView";
import MessagesPage from "./pages/MessagesPage";
import ProfileManager from "./pages/ProfileManager";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Authenticated fetch helper - adds Bearer token and credentials for cross-origin
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("session_token");
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers, credentials: "include" });
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const verifyAuth = async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        navigate("/login", { replace: true });
      }
      setChecking(false);
    };

    verifyAuth();
  }, [checkAuth, navigate]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  return children;
};

// Main App Router
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/software" element={<DigitalAssistantPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/package-selection"
        element={
          <ProtectedRoute>
            <PackageSelection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/itr-filing"
        element={
          <ProtectedRoute>
            <ITRFilingView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounting"
        element={
          <ProtectedRoute>
            <AccountingView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profiles"
        element={
          <ProtectedRoute>
            <ProfileManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/:tab?"
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute allowedRoles={["team_member"]}>
            <TeamDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/activity"
        element={
          <ProtectedRoute allowedRoles={["team_member"]}>
            <TeamActivityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team/client/:clientId"
        element={
          <ProtectedRoute allowedRoles={["team_member", "super_admin"]}>
            <TeamClientDetail />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export { API, BACKEND_URL, authFetch };
export default App;
